import type { TicketStatusCondition as TicketStatusConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { TicketStatusOption } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import * as styles from "./conditions.module.css";

const STATUS_DISPLAY_NAMES: Record<TicketStatusOption, string> = {
  [TicketStatusOption.OPEN]: "Open",
  [TicketStatusOption.CLOSED]: "Closed",
  [TicketStatusOption.SNOOZED]: "Snoozed",
  [TicketStatusOption.IN_PROGRESS]: "In progress",
};

const statusItems: RedoListItem<TicketStatusOption>[] = Object.values(
  TicketStatusOption,
).map((status) => ({
  id: status,
  type: "text" as const,
  text: STATUS_DISPLAY_NAMES[status],
  value: status,
}));

export const TicketStatusCondition = memo(function TicketStatusCondition({
  condition,
  onChange,
}: {
  condition: TicketStatusConditionType;
  onChange: (updated: TicketStatusConditionType) => void;
}) {
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems = useMemo(() => {
    return (condition.statuses || []).map((status) => ({
      id: status,
      type: "text" as const,
      text: STATUS_DISPLAY_NAMES[status],
      value: status,
    }));
  }, [condition.statuses]);

  const displayText =
    (condition.statuses?.length || 0) > 0
      ? condition.statuses!.length === 1
        ? STATUS_DISPLAY_NAMES[condition.statuses![0]!]
        : `${condition.statuses!.length} statuses selected`
      : "Select statuses";

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text color="secondary" fontSize="xs">
        Status
      </Text>
      <RedoFilterDropdownAnchor
        className={styles.fullWidthButton}
        color="primary"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        ref={setDropdownButtonRef}
        text={displayText}
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={statusItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, statuses: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
